import { getProductWizardRoute } from '@/app/config/products.config';
import { useDataTablePageSync, useRequirePermissions } from '@/shared/hooks';
import {
  canCloneGroupLifeProposal,
  PortalStatus,
} from '@/shared/enums/portal-status.enum';
import {
  Avatar,
  DataTable,
  type DataTableColumn,
  Menu,
  Pagination,
  Tag,
} from '@noorden-insurtech/alba-design-system';
import { useNavigate, UseNavigateResult } from '@tanstack/react-router';
import { Ban, CalendarDays, Copy, Edit, Eye, Mail, Pencil, User } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { ProposalSummary } from '../../../services';
import { Broker } from '../../../services/brokers/types';
import {
  canEditCapitalGlobalProposalFromSummary,
  isCapitalGlobalProposalFromSummary,
} from './capital-global/capital-global-proposal-product.util';
import {
  DateRangeFilterField,
  SearchFilterField,
  SelectFilterField,
} from '../../../shared/ui';
import { Page, PageHeader } from '../../../shared/ui/page';
import {
  f,
  formatCurrency,
  formatDateToBrazil,
  useFilters,
} from '../../../shared/utils';
import { formatRegisterNumber } from '../../../shared/utils/format-register-number';
import { useBrokerProducts } from '../../brokers/hooks';
import { useProducts } from '../../quotes/hooks/common/use-products.hook';
import { useSelectedBrokerWriteGuard } from '../../brokers/hooks';
import type { BrokerWriteGuard } from '../../brokers/utils/broker-status.util';
import { toaster } from '../../../shared/ui/toaster.component';
import { useCancelProposal, useProposals, useProposalStatusCounts } from '../hooks';
import {
  useAlterGroupLifeProposal,
  useCloneGroupLifeProposal,
} from '../hooks/group-life';
import { AlterProposalDialog } from './alter-proposal.dialog';
import { CancelProposalDialog } from './cancel-proposal.dialog';
import { CloneProposalDialog } from './pme/clone-proposal.dialog';
import { GroupLifeClonePendingError } from '@/shared/utils/group-life-clone-idempotency.util';
import { PME_PROPOSAL_ACCEPTED_LABEL } from './pme/utils/pme-proposal-lock.util';
import { ProposalStatusCards } from './proposal-status-cards.component';

type TagColorScheme = 'gray' | 'yellow' | 'success' | 'error';

const DEFAULT_SORT_FIELD = 'index_time';
const DEFAULT_SORT_TYPE = 'DESC';

const PROPOSAL_SORT_FIELD_BY_COLUMN_ID: Record<string, string> = {
  proposerName: 'Nome',
  registerNumber: 'CPF',
  brokerName: 'AgentName',
  productName: 'ProductName',
  proposalNumber: 'ProposalNo',
};

const PROPOSAL_COLUMN_ID_BY_SORT_FIELD: Record<string, string> = {
  Nome: 'proposerName',
  CPF: 'registerNumber',
  AgentName: 'brokerName',
  ProductName: 'productName',
  ProposalNo: 'proposalNumber',
};

function isPmeProposal(proposal: ProposalSummary): boolean {
  const productName = (proposal.productName || '').toLowerCase();
  return productName.includes('pme') || productName.includes('vida em grupo');
}

/**
 * Rota do wizard para proposta de produto com backend próprio (registry),
 * retomando o passo pelo status; `undefined` para proposta InsureMO. O
 * wizard é editável (`'new'`); com a escrita travada pela corretora abre
 * somente leitura (`'view'`), para o "Visualizar" continuar funcionando.
 */
function proposalWizardRoute(
  proposal: ProposalSummary,
  flow: 'new' | 'view' = 'new'
) {
  return getProductWizardRoute(
    proposal.productCode,
    proposal.id,
    proposal.proposalStatus,
    flow
  );
}

const proposalStatusConfig: Record<
  string,
  { label: string; colorScheme: TagColorScheme }
> = {
  '6': { label: 'Proposta em preenchimento', colorScheme: 'gray' },
  '12': { label: 'Em assinatura', colorScheme: 'yellow' },
  '7': { label: 'Efetivada', colorScheme: 'success' },
  '8': { label: 'Transmitida', colorScheme: 'success' },
  '9': { label: 'Pendente inspeção', colorScheme: 'yellow' },
  '10': { label: 'Aguardando emissão', colorScheme: 'success' },
  '11': { label: 'Proposta recusada', colorScheme: 'error' },
  '15': { label: 'Proposta expirada', colorScheme: 'gray' },
  '18': { label: 'Proposta em Análise', colorScheme: 'yellow' },
  '19': { label: 'Proposta Recusada pela Análise', colorScheme: 'error' },
  '21': { label: 'Proposta cancelada', colorScheme: 'error' },
};

// Dropdown de status = todos os status individuais (filtro unitário). Os cards
// usam os grupos; os dois se anulam mutuamente porque compartilham o filtro
// `status` (selecionar um sobrescreve o outro).
const statusOptions = [
  { label: 'Todos', value: '' },
  ...Object.entries(proposalStatusConfig).map(([value, config]) => ({
    label: config.label,
    value,
  })),
];

type StatusTagConfig = { label: string; colorScheme: TagColorScheme };

/**
 * Config do tag de status da linha.
 *
 * O código 7 tem significado diferente por produto: no RD Equipamentos é
 * terminal ("Efetivada"); no PME é a aprovação da subscrição — a proposta ainda
 * será assinada e transmitida. Por isso o rótulo do PME é sobrescrito aqui, sem
 * mexer no `proposalStatusConfig`, que vale para os demais produtos.
 */
function resolveStatusTagConfig(proposal: ProposalSummary): StatusTagConfig {
  const config = proposalStatusConfig[proposal.proposalStatus] ?? {
    label: proposal.proposalStatus
      ? `Status ${proposal.proposalStatus}`
      : 'Status -',
    colorScheme: 'gray' as const,
  };

  if (
    isPmeProposal(proposal) &&
    proposal.proposalStatus === String(PortalStatus.ProposalAccepted)
  ) {
    return { ...config, label: PME_PROPOSAL_ACCEPTED_LABEL };
  }

  return config;
}

function canEditProposal(
  proposal: ProposalSummary,
  canWrite: boolean
): boolean {
  if (!canWrite) return false;
  if (isPmeProposal(proposal)) {
    // Em assinatura (12) a proposta não é mais editável — a lista oferece
    // "Visualizar", e é de lá que o corretor chega à etapa de Assinatura.
    // O 7 continua editável: são as aprovadas antes de a aprovação gravar 12.
    return (
      proposal.proposalStatus === '6' ||
      proposal.proposalStatus === String(PortalStatus.ProposalAccepted)
    );
  }
  if (isCapitalGlobalProposalFromSummary(proposal)) {
    return canEditCapitalGlobalProposalFromSummary(proposal);
  }
  return proposal.proposalStatus === '6';
}

const CANCELLABLE_PROPOSAL_STATUSES = new Set<string>([
  String(PortalStatus.ProposalInProgress),
  String(PortalStatus.ProposalAccepted),
  String(PortalStatus.ProposalPendingInspection),
  String(PortalStatus.ProposalInSignature),
  String(PortalStatus.ProposalInAnalysis),
]);

function canCancelProposal(
  proposal: ProposalSummary,
  canWrite: boolean
): boolean {
  return canWrite && CANCELLABLE_PROPOSAL_STATUSES.has(proposal.proposalStatus);
}

function canClonePmeProposalFromList(
  proposal: ProposalSummary,
  canWrite: boolean
): boolean {
  return (
    canWrite &&
    isPmeProposal(proposal) &&
    canCloneGroupLifeProposal(proposal.proposalStatus)
  );
}

function canAlterPmeProposal(
  proposal: ProposalSummary,
  canWrite: boolean
): boolean {
  return isPmeProposal(proposal) && canCancelProposal(proposal, canWrite);
}

function resolveProposalEditRoute(proposal: ProposalSummary): string {
  if (isPmeProposal(proposal)) return '/proposals/$id/pme/edit';
  if (isCapitalGlobalProposalFromSummary(proposal)) {
    return '/proposals/$id/capital-global/edit';
  }
  return '/proposals/$id/edit';
}

function resolveProposalViewRoute(proposal: ProposalSummary): string {
  if (isPmeProposal(proposal)) return '/proposals/$id/pme';
  if (isCapitalGlobalProposalFromSummary(proposal)) {
    return '/proposals/$id/capital-global';
  }
  return '/proposals/$id';
}

function createColumns(
  navigate: UseNavigateResult<string>,
  canWrite: boolean,
  writeGuard: BrokerWriteGuard,
  onCancel: (proposal: ProposalSummary) => void,
  onClone: (proposal: ProposalSummary) => void,
  onAlter: (proposal: ProposalSummary) => void
): DataTableColumn<ProposalSummary>[] {
  const wizardFlow = writeGuard.blocked ? 'view' : 'new';
  return [
    {
      accessorKey: 'proposerName',
      header: 'PROPONENTE',
      size: 240,
      cell: ({ row }) => {
        const proposal = row.original;
        return (
          <div className='flex items-center gap-3'>
            <Avatar.Root size='md' shape='circle' className='bg-alba-blue-500'>
              <User className='w-4 h-4 text-white' />
            </Avatar.Root>
            <div className='flex min-w-0 flex-col gap-0.5'>
              <span className='font-gotham font-bold text-alba-black-500'>
                {proposal.proposerName}
              </span>
              <span className='text-alba-gray-600 text-xs'>
                {formatRegisterNumber(proposal.registerNumber)}
              </span>
              <div className='flex items-center gap-1'>
                <Mail className='w-4 h-4 shrink-0 text-alba-gray-600' />
                <span className='truncate text-alba-gray-600 text-xs'>
                  {proposal.proposerEmail}
                </span>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'brokerName',
      header: 'CORRETOR',
      size: 180,
      cell: ({ row }) => {
        const proposal = row.original;
        return (
          <span className='text-alba-black-500 text-sm font-medium'>
            {(proposal.brokerName || '-').split('\n')[0]}
          </span>
        );
      },
    },
    {
      accessorKey: 'productName',
      header: 'PRODUTO',
      size: 150,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>
          {row.original.productName}
        </span>
      ),
    },
    {
      accessorKey: 'proposalNumber',
      header: 'Nº DA PROPOSTA',
      size: 160,
      cell: ({ row }) => {
        const proposal = row.original;
        return (
          <div className='flex flex-col gap-1'>
            <span className='text-alba-gray-700 text-sm font-medium'>
              {proposal.proposalNumber}
            </span>
            <div className='flex items-center gap-1 text-alba-gray-600 text-xs'>
              <CalendarDays className='w-4 h-4 text-alba-gray-600 mb-0.5' />
              <span>{formatDateToBrazil(proposal.createdAt)}</span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'duePremium',
      header: 'PRÊMIO',
      size: 130,
      sortable: false,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm font-medium tabular-nums'>
          {row.original.duePremium
            ? formatCurrency(row.original.duePremium)
            : '—'}
        </span>
      ),
    },
    {
      accessorKey: 'inclusionUserName',
      header: 'CRIADO POR',
      size: 150,
      sortable: false,
      cell: ({ row }) => (
        <span className='text-alba-gray-700 text-sm'>
          {row.original.inclusionUserName || '-'}
        </span>
      ),
    },
    {
      accessorKey: 'proposalStatus',
      header: 'STATUS',
      size: 140,
      sortable: false,
      cell: ({ row }) => {
        const proposal = row.original;
        const config = resolveStatusTagConfig(proposal);

        return (
          <Tag.Root
            variant='outline'
            colorScheme={config.colorScheme}
            size='sm'
          >
            <Tag.Label>{config.label}</Tag.Label>
          </Tag.Root>
        );
      },
    },
    {
      id: 'actions',
      header: 'AÇÕES',
      size: 50,
      sortable: false,
      cell: ({ row }) => {
        const proposal = row.original;
        const wizardRoute = proposalWizardRoute(proposal, wizardFlow);
        const showEdit = canEditProposal(proposal, canWrite);

        return (
          <div className='flex justify-end'>
            <Menu.Root>
              <Menu.Trigger />
              <Menu.Content>
                {wizardRoute ? (
                  <Menu.Item
                    value='view'
                    icon={<Eye size={16} />}
                    onSelect={() => void navigate(wizardRoute)}
                  >
                    Visualizar
                  </Menu.Item>
                ) : showEdit ? (
                  <>
                    <Menu.Item
                      value='edit'
                      icon={<Edit size={16} />}
                      disabled={writeGuard.blocked}
                      title={writeGuard.reason}
                      onSelect={() => {
                        void navigate({
                          to: resolveProposalEditRoute(proposal),
                          params: { id: proposal.id },
                        });
                      }}
                    >
                      Editar
                    </Menu.Item>
                    {writeGuard.blocked && (
                      <Menu.Item
                        value='view'
                        icon={<Eye size={16} />}
                        onSelect={() => {
                          void navigate({
                            to: resolveProposalViewRoute(proposal),
                            params: { id: proposal.id },
                          });
                        }}
                      >
                        Visualizar
                      </Menu.Item>
                    )}
                  </>
                ) : (
                  <Menu.Item
                    value='view'
                    icon={<Eye size={16} />}
                    onSelect={() => {
                      void navigate({
                        to: resolveProposalViewRoute(proposal),
                        params: { id: proposal.id },
                      });
                    }}
                  >
                    Visualizar
                  </Menu.Item>
                )}
                {canClonePmeProposalFromList(proposal, canWrite) && (
                  <Menu.Item
                    value='clone'
                    icon={<Copy size={16} />}
                    disabled={writeGuard.blocked}
                    title={writeGuard.reason}
                    onSelect={() => onClone(proposal)}
                  >
                    Clonar
                  </Menu.Item>
                )}
                {canCancelProposal(proposal, canWrite) && (
                  <Menu.Item
                    value='cancel'
                    icon={<Ban size={16} />}
                    disabled={writeGuard.blocked}
                    title={writeGuard.reason}
                    onSelect={() => onCancel(proposal)}
                  >
                    Cancelar
                  </Menu.Item>
                )}
                {canAlterPmeProposal(proposal, canWrite) && (
                  <Menu.Item
                    value='alter'
                    icon={<Pencil size={16} />}
                    disabled={writeGuard.blocked}
                    title={writeGuard.reason}
                    onSelect={() => onAlter(proposal)}
                  >
                    Alterar proposta
                  </Menu.Item>
                )}
              </Menu.Content>
            </Menu.Root>
          </div>
        );
      },
    },
  ];
}

export function ProposalsContent({
  selectedBroker,
}: {
  selectedBroker: Broker | null;
}) {
  const navigate = useNavigate();

  const { hasPermission: canWrite } = useRequirePermissions({
    permissions: ['policies.proposals.write'],
  });
  // Com corretora selecionada todas as linhas são dela; em "Todos Corretores"
  // o guard não trava e a Api barra por linha (403).
  const proposalWriteGuard = useSelectedBrokerWriteGuard('proposal');
  const [proposalToCancel, setProposalToCancel] =
    useState<ProposalSummary | null>(null);
  const [proposalToClone, setProposalToClone] =
    useState<ProposalSummary | null>(null);
  const [proposalToAlter, setProposalToAlter] =
    useState<ProposalSummary | null>(null);
  const cancelProposal = useCancelProposal();
  const cloneProposal = useCloneGroupLifeProposal();
  const alterProposal = useAlterGroupLifeProposal();
  const wizardFlow = proposalWriteGuard.blocked ? 'view' : 'new';
  const { filters, debouncedFilters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(500),
    product: f.string().withDefault(''),
    status: f.string().withDefault(''),
    dateFrom: f.string().withDefault(''),
    dateTo: f.string().withDefault(''),
    pageNo: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(10),
    sortField: f.string().withDefault(DEFAULT_SORT_FIELD),
    sortType: f.string().withDefault(DEFAULT_SORT_TYPE),
  });
  const dataTableRef = useDataTablePageSync<ProposalSummary>(filters.pageNo);

  const { data: brokerProducts } = useBrokerProducts(selectedBroker?.code);
  const { data: products } = useProducts();

  // Com um corretor selecionado, exibe apenas os produtos ativos daquele
  // corretor (inclui RD Equipamentos quando configurado). Sem corretor
  // ("Todos Corretores"), exibe todo o catálogo InsureMO.
  const productOptions = useMemo(() => {
    const items = selectedBroker
      ? (brokerProducts?.data ?? [])
          .filter(product => product.status === 'active')
          .map(product => ({ label: product.name, value: product.code }))
      : [...(products ?? [])]
          .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
          .map(p => ({ label: p.name, value: p.code }));
    return [{ label: 'Todos os produtos', value: '' }, ...items];
  }, [selectedBroker, brokerProducts, products]);

  const { counts: statusCounts, isLoading: isLoadingCounts } =
    useProposalStatusCounts({
      partyCode: selectedBroker?.code,
      productCode: debouncedFilters.product || undefined,
      proposalDateFrom: debouncedFilters.dateFrom || undefined,
      proposalDateTo: debouncedFilters.dateTo || undefined,
    });

  // Card e dropdown compartilham o filtro `status`: selecionar um sobrescreve o
  // outro (mutuamente exclusivos). O card ativo é derivado de `filters.status`.
  const handleSelectStatusCard = (status: string | null) => {
    setFilters({ ...filters, status: status ?? '', pageNo: 1 });
  };

  const { data, isLoading, error } = useProposals({
    pageNumber: debouncedFilters.pageNo,
    pageSize: debouncedFilters.pageSize,
    search: debouncedFilters.search,
    status: debouncedFilters.status,
    productCode: debouncedFilters.product || undefined,
    proposalDateFrom: debouncedFilters.dateFrom || undefined,
    proposalDateTo: debouncedFilters.dateTo || undefined,
    partyCode: selectedBroker?.code,
    sortField: filters.sortField,
    sortType: filters.sortType === 'DESC' ? 'DESC' : 'ASC',
  });

  const columns = useMemo(
    () =>
      createColumns(
        navigate,
        canWrite ?? false,
        proposalWriteGuard,
        setProposalToCancel,
        setProposalToClone,
        setProposalToAlter
      ),
    [navigate, canWrite, proposalWriteGuard]
  );
  const proposals = data?.data || [];
  const totalPages = data?.meta?.totalPages || 0;
  const totalItems = data?.meta?.total || 0;
  const sorting = useMemo(() => {
    const columnId = PROPOSAL_COLUMN_ID_BY_SORT_FIELD[filters.sortField];

    if (!columnId) return [];

    return [
      {
        id: columnId,
        desc: filters.sortType === 'DESC',
      },
    ];
  }, [filters.sortField, filters.sortType]);

  return (
    <>
      <div className='pt-4'>
        <ProposalStatusCards
          counts={statusCounts}
          activeStatus={filters.status || null}
          onSelectStatus={handleSelectStatusCard}
          isLoading={isLoadingCounts}
        />
      </div>

      <PageHeader.Filters>
        <div className='flex flex-col gap-3 md:flex-1 md:flex-row md:items-center md:gap-4 lg:max-w-4xl'>
          <SearchFilterField
            {...registerFilter('search')}
            placeholder='Buscar por nome, CNPJ, responsável ou email...'
            className='w-full'
          />
          <SelectFilterField
            {...registerFilter('status')}
            items={statusOptions}
            placeholder='Status'
            className='w-full md:min-w-[200px]'
            onValueChange={value =>
              setFilters({ ...filters, status: value, pageNo: 1 })
            }
          />
          <SelectFilterField
            {...registerFilter('product')}
            items={productOptions}
            placeholder='Produto'
            className='w-full md:min-w-[200px]'
            onValueChange={value =>
              setFilters({ ...filters, product: value, pageNo: 1 })
            }
          />
          <DateRangeFilterField
            from={filters.dateFrom}
            to={filters.dateTo}
            placeholder='Data da proposta'
            className='w-full md:min-w-[200px]'
            onChange={range =>
              setFilters({
                ...filters,
                dateFrom: range.from,
                dateTo: range.to,
                pageNo: 1,
              })
            }
          />
        </div>
      </PageHeader.Filters>

      <Page.Content className='min-w-0'>
        {error ? (
          <div className='py-8 text-center text-alba-error-red-500'>
            Erro ao carregar propostas. Tente novamente.
          </div>
        ) : (
          <>
            <div className='space-y-4 md:hidden'>
              {isLoading
                ? Array.from({ length: Math.min(filters.pageSize, 3) }).map(
                    (_, index) => (
                      <div
                        key={index}
                        className='h-60 animate-pulse rounded-3xl bg-alba-gray-200'
                      />
                    )
                  )
                : proposals.map(proposal => {
                    const config = resolveStatusTagConfig(proposal);
                    const wizardRoute = proposalWizardRoute(
                      proposal,
                      wizardFlow
                    );
                    const showEdit = canEditProposal(proposal, canWrite);

                    return (
                      <article
                        key={proposal.id}
                        className='rounded-3xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'
                      >
                        <div className='flex items-start justify-between gap-3'>
                          <div className='flex min-w-0 items-center gap-3'>
                            <Avatar.Root
                              size='md'
                              shape='circle'
                              className='bg-alba-blue-500'
                            >
                              <User className='w-4 h-4 text-white' />
                            </Avatar.Root>
                            <div className='min-w-0'>
                              <p className='truncate font-gotham font-bold text-alba-black-500'>
                                {proposal.proposerName}
                              </p>
                              <p className='mt-1 truncate text-xs text-alba-gray-600'>
                                {proposal.proposerEmail}
                              </p>
                            </div>
                          </div>
                          <Menu.Root>
                            <Menu.Trigger />
                            <Menu.Content>
                              {wizardRoute ? (
                                <Menu.Item
                                  value='view'
                                  icon={<Eye size={16} />}
                                  onSelect={() => void navigate(wizardRoute)}
                                >
                                  Visualizar
                                </Menu.Item>
                              ) : showEdit ? (
                                <>
                                  <Menu.Item
                                    value='edit'
                                    icon={<Edit size={16} />}
                                    disabled={proposalWriteGuard.blocked}
                                    title={proposalWriteGuard.reason}
                                    onSelect={() => {
                                      void navigate({
                                        to: resolveProposalEditRoute(proposal),
                                        params: { id: proposal.id },
                                      });
                                    }}
                                  >
                                    Editar
                                  </Menu.Item>
                                  {proposalWriteGuard.blocked && (
                                    <Menu.Item
                                      value='view'
                                      icon={<Eye size={16} />}
                                      onSelect={() => {
                                        void navigate({
                                          to: resolveProposalViewRoute(
                                            proposal
                                          ),
                                          params: { id: proposal.id },
                                        });
                                      }}
                                    >
                                      Visualizar
                                    </Menu.Item>
                                  )}
                                </>
                              ) : (
                                <Menu.Item
                                  value='view'
                                  icon={<Eye size={16} />}
                                  onSelect={() => {
                                    void navigate({
                                      to: resolveProposalViewRoute(proposal),
                                      params: { id: proposal.id },
                                    });
                                  }}
                                >
                                  Visualizar
                                </Menu.Item>
                              )}
                              {canClonePmeProposalFromList(
                                proposal,
                                canWrite ?? false
                              ) && (
                                <Menu.Item
                                  value='clone'
                                  icon={<Copy size={16} />}
                                  disabled={proposalWriteGuard.blocked}
                                  title={proposalWriteGuard.reason}
                                  onSelect={() => setProposalToClone(proposal)}
                                >
                                  Clonar
                                </Menu.Item>
                              )}
                              {canCancelProposal(proposal, canWrite ?? false) && (
                                <Menu.Item
                                  value='cancel'
                                  icon={<Ban size={16} />}
                                  disabled={proposalWriteGuard.blocked}
                                  title={proposalWriteGuard.reason}
                                  onSelect={() => setProposalToCancel(proposal)}
                                >
                                  Cancelar
                                </Menu.Item>
                              )}
                              {canAlterPmeProposal(proposal, canWrite ?? false) && (
                                <Menu.Item
                                  value='alter'
                                  icon={<Pencil size={16} />}
                                  disabled={proposalWriteGuard.blocked}
                                  title={proposalWriteGuard.reason}
                                  onSelect={() => setProposalToAlter(proposal)}
                                >
                                  Alterar proposta
                                </Menu.Item>
                              )}
                            </Menu.Content>
                          </Menu.Root>
                        </div>

                        <div className='mt-4 space-y-2'>
                          <div className='flex items-center justify-between gap-3'>
                            <p className='text-sm font-semibold text-alba-black-500'>
                              CPF/CNPJ
                            </p>
                            <p className='truncate text-sm text-alba-gray-700'>
                              {formatRegisterNumber(proposal.registerNumber)}
                            </p>
                          </div>
                          <div className='flex items-center justify-between gap-3'>
                            <p className='text-sm font-semibold text-alba-black-500'>
                              Corretor
                            </p>
                            <p className='truncate text-sm text-alba-gray-700'>
                              {(proposal.brokerName || '').split('\n')[0]}
                            </p>
                          </div>
                          <div className='flex items-center justify-between gap-3'>
                            <p className='text-sm font-semibold text-alba-black-500'>
                              Produto
                            </p>
                            <p className='truncate text-sm text-alba-gray-700'>
                              {proposal.productName}
                            </p>
                          </div>
                          <div className='flex items-center justify-between gap-3'>
                            <p className='text-sm font-semibold text-alba-black-500'>
                              N° da Proposta
                            </p>
                            <div className='flex flex-col items-end gap-0.5 text-right'>
                              <p className='text-sm text-alba-gray-700'>
                                {proposal.proposalNumber}
                              </p>
                              <div className='flex items-center gap-1 text-xs text-alba-gray-600'>
                                <CalendarDays className='h-3.5 w-3.5' />
                                <span>
                                  {formatDateToBrazil(proposal.createdAt)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className='flex items-center justify-between gap-3'>
                            <p className='text-sm font-semibold text-alba-black-500'>
                              Criado por
                            </p>
                            <p className='truncate text-sm text-alba-gray-700'>
                              {proposal.inclusionUserName || '-'}
                            </p>
                          </div>
                          <div className='flex items-center justify-between gap-3'>
                            <p className='text-sm font-semibold text-alba-black-500'>
                              Status
                            </p>
                            <Tag.Root
                              variant='outline'
                              colorScheme={config.colorScheme}
                              size='sm'
                            >
                              <Tag.Label>{config.label}</Tag.Label>
                            </Tag.Root>
                          </div>
                          <div className='flex items-center justify-between gap-3'>
                            <p className='text-sm font-semibold text-alba-black-500'>
                              Prêmio
                            </p>
                            <p className='truncate text-sm text-alba-gray-700 tabular-nums'>
                              {proposal.duePremium
                                ? formatCurrency(proposal.duePremium)
                                : '—'}
                            </p>
                          </div>
                        </div>
                      </article>
                    );
                  })}

              {!isLoading && !proposals.length && (
                <div className='rounded-3xl bg-white p-6 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
                  Nenhuma proposta encontrada.
                </div>
              )}

              {totalPages > 0 && (
                <div className='flex justify-center'>
                  <Pagination
                    count={totalPages * filters.pageSize}
                    page={filters.pageNo}
                    pageSize={filters.pageSize}
                    totalItems={totalItems}
                    pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                    onPageChange={page =>
                      setFilters({
                        ...filters,
                        pageNo: page.page,
                        pageSize: page.pageSize,
                      })
                    }
                    onPageSizeSelect={pageSize =>
                      setFilters({ ...filters, pageNo: 1, pageSize })
                    }
                  />
                </div>
              )}
            </div>

            <div className='hidden min-w-0 overflow-hidden rounded-3xl md:block'>
              <DataTable
                ref={dataTableRef}
                className='min-w-0'
                loading={isLoading}
                columns={columns}
                pageSizeOptions={[5, 10, 20, 30, 40, 50]}
                totalPages={totalPages}
                data={proposals}
                pageSize={filters.pageSize}
                totalItems={totalItems}
                manualSorting
                sorting={sorting}
                onSortChange={nextSorting => {
                  const nextSort = nextSorting[0];
                  const sortField = nextSort
                    ? PROPOSAL_SORT_FIELD_BY_COLUMN_ID[nextSort.id]
                    : DEFAULT_SORT_FIELD;

                  setFilters({
                    ...filters,
                    pageNo: 1,
                    sortField: sortField ?? DEFAULT_SORT_FIELD,
                    sortType: nextSort?.desc ? 'DESC' : 'ASC',
                  });
                }}
                onPageChange={(page, pageSize) =>
                  setFilters({ ...filters, pageNo: page, pageSize })
                }
              />
            </div>
          </>
        )}
      </Page.Content>
      <CloneProposalDialog
        isOpen={proposalToClone != null}
        isLoading={cloneProposal.isPending}
        onClose={() => {
          if (cloneProposal.isPending) return;
          setProposalToClone(null);
        }}
        onConfirm={() => {
          if (!proposalToClone || cloneProposal.isPending) return;
          cloneProposal.mutate(
            { policyId: proposalToClone.id },
            {
              onSuccess: data => {
                setProposalToClone(null);
                toaster.create({
                  type: 'success',
                  title: 'Proposta clonada',
                  description: `Nova proposta Nº ${data.proposalNumber} criada em digitação.`,
                });
                void navigate({
                  to: '/proposals/$id/pme/edit',
                  params: { id: data.policyId },
                });
              },
              onError: error => {
                if (error instanceof GroupLifeClonePendingError) {
                  setProposalToClone(null);
                  toaster.create({
                    type: 'info',
                    title: 'Clonar proposta',
                    description: error.message,
                  });
                  return;
                }
                toaster.create({
                  type: 'error',
                  title: 'Não foi possível clonar',
                  description:
                    error instanceof Error ? error.message : 'Tente novamente.',
                });
              },
            }
          );
        }}
      />
      <AlterProposalDialog
        isOpen={proposalToAlter != null}
        isLoading={alterProposal.isPending}
        onClose={() => {
          if (alterProposal.isPending) return;
          setProposalToAlter(null);
        }}
        onConfirm={() => {
          if (!proposalToAlter || alterProposal.isPending) return;
          alterProposal.mutate(
            { policyId: proposalToAlter.id },
            {
              onSuccess: data => {
                setProposalToAlter(null);
                toaster.create({
                  type: 'success',
                  title: 'Proposta alterada',
                  description: `Nova proposta Nº ${data.proposalNumber} criada em digitação.`,
                });
                void navigate({
                  to: '/proposals/$id/pme/edit',
                  params: { id: data.policyId },
                });
              },
              onError: error => {
                if (error instanceof GroupLifeClonePendingError) {
                  setProposalToAlter(null);
                  toaster.create({
                    type: 'info',
                    title: 'Alterar proposta',
                    description: error.message,
                  });
                  return;
                }
                toaster.create({
                  type: 'error',
                  title: 'Não foi possível alterar',
                  description:
                    error instanceof Error ? error.message : 'Tente novamente.',
                });
              },
            }
          );
        }}
      />
      <CancelProposalDialog
        isOpen={proposalToCancel != null}
        isLoading={cancelProposal.isPending}
        proposerName={proposalToCancel?.proposerName}
        onClose={() => setProposalToCancel(null)}
        onConfirm={async () => {
          if (!proposalToCancel) return;
          try {
            await cancelProposal.mutateAsync(proposalToCancel.id);
            toaster.create({
              type: 'success',
              title: 'Proposta cancelada',
            });
            setProposalToCancel(null);
          } catch (error) {
            toaster.create({
              type: 'error',
              title: 'Não foi possível cancelar',
              description:
                error instanceof Error ? error.message : 'Tente novamente.',
            });
          }
        }}
      />
    </>
  );
}
